"use client";
import React, { useState } from "react";
import Link from "next/link";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useRouter } from "next/navigation";
import { decode } from "html-entities";
import { v4 as uuidv4 } from "uuid";
import CloseSvg from "@/components/svg/header/close";
import { LanguageSwitcher } from "@/app/[lang]/(main)/_components/LanguageSwitcher";
import type { Category, Child, Information } from "@/types/header";

interface MobileMenuProps {
  mobileMenu: boolean;
  setMobileMenu: (mobileMenu: boolean) => void;
  categories: Category[];
  contact: string;
  informations: Information[];
}

const HeaderMobileMenu: React.FC<MobileMenuProps> = ({
  mobileMenu,
  setMobileMenu,
  categories,
  contact,
  informations,
}) => {
  const [path, setPath] = useState<{ name: string; href: string }[]>([]);
  const { i18n } = useLingui();
  const router = useRouter();

  const handleCategoryClick = (categoryName: string, categoryHref: string) => {
    const newPath = {
      name: categoryName,
      href: `/category/${categoryHref.split("/").pop()}`,
    };
    setPath([...path, newPath]);
  };

  const handleBackClick = () => {
    setPath(path.slice(0, -1));
  };

  const currentCategory = path.reduce(
    (current, item) =>
      current?.children?.find((cat: Category) => cat.name === item.name) ||
      current?.grand_childs?.find((cat: Child) => cat.name === item.name) ||
      current,
    { name: "root", children: categories || [] } as any,
  );

  const handleCategorySelection = (item: Category | Child) => {
    if ((item as Category)?.children?.length > 0 || (item as Child)?.grand_childs?.length > 0) {
      handleCategoryClick(item.name, item.href);
    } else {
      router.push(`/category/${item.href.split("/").pop()}`);
      setMobileMenu(false);
      setPath([]);
    }
  };

  const renderCategoryItems = (items: (Category | Child)[]) =>
    items.map((item) => (
      <li
        key={uuidv4()}
        className="flex cursor-pointer items-center justify-between border-b border-[#eee] py-[15px]"
        onClick={() => handleCategorySelection(item)}
      >
        <p className="font-medium">{decode(item.name)}</p>
        {((item as Category)?.children?.length > 0 ||
          (item as Child)?.grand_childs?.length > 0) && (
          <svg
            className="h-4 w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M6.23 20.23 8 22l10-10L8 2 6.23 3.77 14.46 12z" />
          </svg>
        )}
      </li>
    ));

  const goToInformation = (seo_url: any): void => {
    router.push(`/information/${seo_url}`);
    setMobileMenu(false);
    setPath([]);
  };

  return (
    <div
      className={`fixed left-0 top-0 z-50 block h-screen w-full overflow-y-scroll bg-white transition-all duration-500 ease-out md:hidden ${
        mobileMenu ? "translate-x-0" : "-translate-x-full"
      }`}
      style={{ minHeight: "-webkit-fill-available" }}
    >
      {/* Header */}
      <div className="flex w-full items-center justify-between border-b-2 border-[#eee] px-[15px] py-[10px]">
        <div className="h-6 w-6">
          {path.length > 0 && (
            <button onClick={handleBackClick} className="p-0 text-black">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
        </div>
        <h2 className="text-center text-[24px] font-semibold">
          {path.length > 0 ? decode(path[path.length - 1].name) : t(i18n)`Menu`}
        </h2>
        <div
          onClick={() => {
            setMobileMenu(false);
            setPath([]);
          }}
          className="flex w-[20px] cursor-pointer items-center"
        >
          <CloseSvg />
        </div>
      </div>

      {/* Category List */}
      <ul className="px-[14px]">
        {renderCategoryItems(currentCategory?.children || currentCategory?.grand_childs || [])}
      </ul>

      {path.length > 0 ? (
        <div
          className="m-[10px] mb-[100px] cursor-pointer bg-[#eee]"
          onClick={() => {
            router.push(path[path.length - 1].href);
            setMobileMenu(false);
          }}
        >
          <p className="px-2.5 px-[10px] py-4 text-[15px]">
            <Trans>Show all {decode(path[path.length - 1].name)}</Trans>
          </p>
        </div>
      ) : (
        <>
          <div className="relative border-t-[15px] border-t-[#f5f5f5]">
            <div className="mx-[14px] mb-[16px] mt-[8px]">
              <Link
                prefetch={false}
                href="/create-gift"
                onClick={() => {
                  setMobileMenu(false);
                  setPath([]);
                }}
              >
                <button className="btn-outlined w-full rounded border border-gray-300 py-2">
                  <Trans>Gift Designer</Trans>
                </button>
              </Link>
            </div>

            <ul className="px-[14px]">
              <Link href={contact} prefetch={false}>
                <li className="flex cursor-pointer items-center justify-between border-b border-[#eee] py-[15px]">
                  <p className="font-medium">
                    <Trans>Contact</Trans>
                  </p>
                </li>
              </Link>
              {informations.map((item: Information) => (
                <li
                  onClick={() => goToInformation(item.seo_url)}
                  key={uuidv4()}
                  className="flex cursor-pointer items-center justify-between border-b border-[#eee] py-[15px]"
                >
                  <p className="font-medium">{decode(item.title)}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative border-t-[15px] border-t-[#f5f5f5] px-[14px] pb-[100px] pt-[16px]">
            <LanguageSwitcher darkMode={false} />
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderMobileMenu;
